import { FormEvent, useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import { store } from "store"
import { useUpdateClientMutation } from "store/api/clientApi"
import { useLazyGetCountriesQuery, useLazyGetStatesQuery, useLazyGetZipCodesQuery } from "store/api/dictionaryApi"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconTag } from "assets/svg/icon-tag.svg"

import { getAddressLabelsByCountry } from "utils/addressUtils"
import { convertDataToOptions } from "utils/convertData"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { ButtonNew } from "components/form/ButtonNew"
import CitySuburbSelect from "components/form/CitySuburbSelect"
import { InputNew } from "components/form/InputNew"
import { SearchFnType, SelectDynamic } from "components/form/SelectDynamic"

import { useCustomerDashboard } from "../CustomerDashboardContext"

const DevicesDataWrapper = styled.section`
  margin-bottom: 24px;
`

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  padding: 0 20px 20px;
`

const FieldItem = styled.div`
  flex: 1 0 calc(33.33% - 20px);
  min-width: 200px;
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldLabel = styled.h4`
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--text-primary);
  margin: 0 0 4px 0;
`

const getInstallationType = (device: UITypes.Device): string => {
  const type = device.installationType
  if (type) {
    if (typeof type === "string") {
      return type
    }
    return type[0]?.value as string
  }
  return ""
}

interface AddressInformationProps {
  deviceValues: UITypes.Device
  setDeviceValues: (values: UITypes.Device) => void
  originalDevice: ApiTypes.Model.Device
  errors: { deviceName?: string; address1?: string }
  setErrors: (errors: { deviceName?: string; address1?: string }) => void
}

export const AddressInformation = ({
  deviceValues,
  setDeviceValues,
  originalDevice,
  errors,
  setErrors,
}: AddressInformationProps) => {
  const { customer, onUpdateSuccess } = useCustomerDashboard()
  const [updateClient, { isLoading }] = useUpdateClientMutation()
  const [addressErrors, setAddressErrors] = useState<{ city?: string; state?: string; zip?: string }>({})

  const addressLabels = getAddressLabelsByCountry(deviceValues.country?.[0]?.label)

  const onDeviceValuesChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setDeviceValues({ ...deviceValues, [name]: value })
    if (errors[name as keyof typeof errors]) {
      setErrors({ ...errors, [name]: undefined })
    }
  }

  const searchFn: SearchFnType = (option, inputValue) => {
    let search = inputValue
    if (deviceValues.country?.[0]?.value === 2) {
      search = search.slice(0, 3)
    }
    return option.label.toLowerCase().includes(search.toLowerCase())
  }

  const handleCountryChange = (country: UITypes.Option[]) => {
    if (deviceValues.country?.[0]?.value === country[0]?.value) {
      return
    }
    setDeviceValues({ ...deviceValues, ...{ country, zip: [], state: [], city: [], suburb: [] } })
  }

  const handleZipChange = (zip: UITypes.Option[]) => {
    const zipData = store.getState().tempData.currentZip

    if (addressErrors.zip) {
      setAddressErrors({ ...addressErrors, zip: undefined })
    }

    if (zipData?.city) {
      const city = convertDataToOptions({ data: [zipData.city] }, "id", "name")
      const state = convertDataToOptions({ data: [zipData.state] }, "id", "name")
      const suburb = convertDataToOptions(
        { data: zipData.suburbs?.length > 0 ? [zipData.suburbs[0]] : null },
        "id",
        "name",
      )

      if (city && state) {
        setDeviceValues({
          ...deviceValues,
          zip,
          city,
          state,
          ...(suburb && suburb.length > 0 ? { suburb } : {}),
        })
        setAddressErrors({ zip: undefined, city: undefined, state: undefined })
        return
      }
    }

    setDeviceValues({ ...deviceValues, zip })
  }

  const renderCitySuburb = () => {
    const showSuburb = deviceValues.country?.[0]?.value === 3
    const entity = showSuburb ? "suburb" : "city"
    const values = showSuburb ? deviceValues.suburb : deviceValues.city

    return (
      <CitySuburbSelect
        showSuburb={showSuburb}
        zip_code={deviceValues.zip?.[0]?.label || ""}
        state_id={deviceValues.state?.[0]?.value != null ? String(deviceValues.state[0].value) : undefined}
        country={deviceValues.country?.[0]?.label || ""}
        values={values || []}
        skipLabel
        onChange={value => {
          setDeviceValues({ ...deviceValues, ...{ [entity]: value } })
          if (addressErrors.city) {
            setAddressErrors({ ...addressErrors, city: undefined })
          }
        }}
        error={addressErrors.city || ""}
      />
    )
  }

  const handleSaveAddress = async () => {
    if (!customer || !deviceValues || !originalDevice) return

    const validationErrors: { deviceName?: string; address1?: string } = {}
    const addressValidationErrors: { city?: string; state?: string; zip?: string } = {}

    if (!deviceValues.address1 || deviceValues.address1.trim() === "") {
      validationErrors.address1 = "Address is required"
    }

    if (!deviceValues.zip || deviceValues.zip.length === 0) {
      addressValidationErrors.zip = "Zip code is required"
    }

    if (!deviceValues.state || deviceValues.state.length === 0) {
      addressValidationErrors.state = "State is required"
    }

    const showSuburb = deviceValues.country?.[0]?.value === 3
    const cityOrSuburb = showSuburb ? deviceValues.suburb : deviceValues.city
    if (!cityOrSuburb || cityOrSuburb.length === 0) {
      addressValidationErrors.city = showSuburb ? "Suburb is required" : "City is required"
    }

    const deviceNameToValidate = deviceValues.deviceName || originalDevice.name || ""
    if (!deviceNameToValidate || deviceNameToValidate.trim() === "") {
      validationErrors.deviceName = "Device name is required"
    }

    if (Object.keys(validationErrors).length > 0 || Object.keys(addressValidationErrors).length > 0) {
      setErrors(validationErrors)
      setAddressErrors(addressValidationErrors)
      return
    }

    setErrors({})
    setAddressErrors({})

    try {
      const customerId = customer.id || 0

      const installationType = getInstallationType(deviceValues)
      const editDevices: ApiTypes.Req.EditCustomerDevice[] = [
        {
          sn: originalDevice.sn,
          name: deviceValues.deviceName || originalDevice.name || "",
          address1: deviceValues.address1 || "",
          address2: deviceValues.address2 || "",
          state: deviceValues.state?.[0] ? Number(deviceValues.state[0].value) : originalDevice.state?.id || undefined,
          city: deviceValues.city?.[0] ? Number(deviceValues.city[0].value) : originalDevice.city?.id || undefined,
          country_id: deviceValues.country?.[0]
            ? Number(deviceValues.country[0].value)
            : originalDevice.country?.id || 0,
          zip_code: (deviceValues.zip?.[0]?.label as string) || originalDevice.zip?.code || "",
          where_installed_id: Number(deviceValues.deviceLocation?.[0]?.value ?? originalDevice.where_installed_id),
          resident_type_id: originalDevice.residence_type?.id || 0,
          installation_type: installationType,
          suburb: deviceValues.suburb?.[0] ? String(deviceValues.suburb[0].value) : undefined,
          ...(originalDevice.system_age ? { system_age: Number(originalDevice.system_age) } : {}),
        },
      ]

      await updateClient({
        id: customerId,
        body: {
          devices: editDevices,
        } as unknown as ApiTypes.Req.ClientDevices,
      }).unwrap()

      toast.success("Address information updated successfully")
      onUpdateSuccess()
    } catch {
      toast.error("Failed to update address information")
    }
  }

  return (
    <DevicesDataWrapper>
      <ModalSectionTitle title="Address Information" Icon={IconTag}>
        <ButtonNew onClick={handleSaveAddress} disabled={isLoading} color="blue" text="Save" />
      </ModalSectionTitle>
      <FieldRow>
        <FieldItem>
          <FieldWrapper>
            <FieldLabel>Address 1</FieldLabel>
            <InputNew
              placeholder="Enter Address"
              name="address1"
              value={deviceValues.address1}
              onChange={onDeviceValuesChange}
              errorMessage={errors.address1 || ""}
            />
          </FieldWrapper>
        </FieldItem>
        <FieldItem>
          <FieldWrapper>
            <FieldLabel>Address 2</FieldLabel>
            <InputNew
              placeholder="Enter Address"
              name="address2"
              value={deviceValues.address2 || ""}
              onChange={onDeviceValuesChange}
              errorMessage=""
            />
          </FieldWrapper>
        </FieldItem>
        <FieldItem>
          <FieldWrapper>
            <FieldLabel>Country</FieldLabel>
            <SelectDynamic
              valueName="id"
              labelName="name"
              name="country"
              useLazyQuery={useLazyGetCountriesQuery}
              placeholder="Enter country"
              values={deviceValues.country || []}
              isSearchable={false}
              onChange={handleCountryChange}
              errorMessage=""
            />
          </FieldWrapper>
        </FieldItem>
        <FieldItem>
          <FieldWrapper>
            <FieldLabel>{addressLabels.zip}</FieldLabel>
            <SelectDynamic
              valueName="id"
              labelName="code"
              name="zip"
              searchFieldName="code"
              useLazyQuery={useLazyGetZipCodesQuery}
              placeholder={`Enter ${addressLabels.zip}`}
              values={deviceValues.zip || []}
              searchFn={searchFn}
              onChange={handleZipChange}
              queryParams={{ country_id: (deviceValues.country?.[0]?.value || 1) as unknown as number }}
              errorMessage={addressErrors.zip || ""}
            />
          </FieldWrapper>
        </FieldItem>
        <FieldItem>
          <FieldWrapper>
            <FieldLabel>{addressLabels.city}</FieldLabel>
            {renderCitySuburb()}
          </FieldWrapper>
        </FieldItem>
        <FieldItem>
          <FieldWrapper>
            <FieldLabel>{addressLabels.state}</FieldLabel>
            <SelectDynamic
              valueName="id"
              labelName="name"
              placeholder={`Enter ${addressLabels.state}`}
              name="state"
              useLazyQuery={useLazyGetStatesQuery}
              values={deviceValues.state || []}
              onChange={value => {
                setDeviceValues({ ...deviceValues, ...{ state: value } })
                if (addressErrors.state) {
                  setAddressErrors({ ...addressErrors, state: undefined })
                }
              }}
              errorMessage={addressErrors.state || ""}
              queryParams={{ country_id: (deviceValues.country?.[0]?.value || 1) as unknown as number }}
            />
          </FieldWrapper>
        </FieldItem>
      </FieldRow>
    </DevicesDataWrapper>
  )
}
